/**
 * Template type definitions and per-agent-type lookup maps for report generation.
 *
 * The full template definition (display data + sections) lives in the
 * canvas_templates DB table. This file provides:
 *   - TypeScript interfaces (DocumentTemplate, TemplateSection)
 *   - Per-agent-type enrichment maps (SUMMARY_KEYS, INSTRUCTION_EXAMPLE_KEYS)
 *   - ADDITIONAL_SECTIONS for the "add section" picker
 *   - Helper functions (getAllSections, getAgentTypes)
 *
 * Agent type IDs must match backend SUB_AGENT_TYPES in cloud_run/byggve/report_generation.py.
 */

export interface TemplateSection {
  agentType: string;
  headerKey: string;
  summaryKey: string;
  blockType: "markdown" | "risks" | "contradictions" | "timeline" | "requirements";
  instructionExampleKey?: string;
}

export interface DocumentTemplate {
  id: string;
  name: string;
  description: string;
  badge?: string;
  sections: TemplateSection[];
}

export const INSTRUCTION_EXAMPLE_KEYS: Record<string, string> = {
  executive_summary: "createReportModal.customInstructionsExamples.executive_summary",
  key_findings: "createReportModal.customInstructionsExamples.key_findings",
  risk_analyst: "createReportModal.customInstructionsExamples.risk_analyst",
  timeline_extractor: "createReportModal.customInstructionsExamples.timeline_extractor",
  contradiction_detector: "createReportModal.customInstructionsExamples.contradiction_detector",
  contract_compliance: "createReportModal.customInstructionsExamples.contract_compliance",
  commercial_terms: "createReportModal.customInstructionsExamples.commercial_terms",
  operational_requirements: "createReportModal.customInstructionsExamples.operational_requirements",
  bid_evaluation_analysis: "createReportModal.customInstructionsExamples.bid_evaluation_analysis",
  regulatory_requirements: "createReportModal.customInstructionsExamples.regulatory_requirements",
  scope_analysis: "createReportModal.customInstructionsExamples.scope_analysis",
  boundary_coordination: "createReportModal.customInstructionsExamples.boundary_coordination",
  documentation_requirements:
    "createReportModal.customInstructionsExamples.documentation_requirements",
  question_list_generator: "createReportModal.customInstructionsExamples.question_list_generator",
  assumptions_reservations_generator:
    "createReportModal.customInstructionsExamples.assumptions_reservations_generator",
  procurement: "createReportModal.customInstructionsExamples.procurement",
  mandatory_requirements: "createReportModal.customInstructionsExamples.mandatory_requirements",
  missing_documents_detector:
    "createReportModal.customInstructionsExamples.missing_documents_detector",
  // Go/No-Go and bid analysis agents
  project_snapshot: "createReportModal.customInstructionsExamples.project_snapshot",
  mandatory_requirements_eligibility:
    "createReportModal.customInstructionsExamples.mandatory_requirements_eligibility",
  mandatory_requirements_execution:
    "createReportModal.customInstructionsExamples.mandatory_requirements_execution",
  risk_analyst_go_no_go: "createReportModal.customInstructionsExamples.risk_analyst_go_no_go",
  document_checklist: "createReportModal.customInstructionsExamples.document_checklist",
  // Technical analysis agents (split from deprecated technical_analyst)
  material_requirements: "createReportModal.customInstructionsExamples.material_requirements",
  // Teknisk analys template (consolidated 3-section version)
  scope_interfaces: "createReportModal.customInstructionsExamples.scope_interfaces",
  site_execution_conditions:
    "createReportModal.customInstructionsExamples.site_execution_conditions",
};

export const SUMMARY_KEYS: Record<string, string> = {
  executive_summary: "createReportModal.sectionSummaries.executive_summary",
  key_findings: "createReportModal.sectionSummaries.key_findings",
  risk_analyst: "createReportModal.sectionSummaries.risk_analyst",
  contradiction_detector: "createReportModal.sectionSummaries.contradiction_detector",
  contract_compliance: "createReportModal.sectionSummaries.contract_compliance",
  commercial_terms: "createReportModal.sectionSummaries.commercial_terms",
  operational_requirements: "createReportModal.sectionSummaries.operational_requirements",
  bid_evaluation_analysis: "createReportModal.sectionSummaries.bid_evaluation_analysis",
  regulatory_requirements: "createReportModal.sectionSummaries.regulatory_requirements",
  scope_analysis: "createReportModal.sectionSummaries.scope_analysis",
  boundary_coordination: "createReportModal.sectionSummaries.boundary_coordination",
  documentation_requirements: "createReportModal.sectionSummaries.documentation_requirements",
  question_list_generator: "createReportModal.sectionSummaries.question_list_generator",
  assumptions_reservations_generator:
    "createReportModal.sectionSummaries.assumptions_reservations_generator",
  timeline_extractor: "createReportModal.sectionSummaries.timeline_extractor",
  procurement: "createReportModal.sectionSummaries.procurement",
  mandatory_requirements: "createReportModal.sectionSummaries.mandatory_requirements",
  missing_documents_detector: "createReportModal.sectionSummaries.missing_documents_detector",
  // Go/No-Go and bid analysis agents
  project_snapshot: "createReportModal.sectionSummaries.project_snapshot",
  mandatory_requirements_eligibility:
    "createReportModal.sectionSummaries.mandatory_requirements_eligibility",
  mandatory_requirements_execution:
    "createReportModal.sectionSummaries.mandatory_requirements_execution",
  risk_analyst_go_no_go: "createReportModal.sectionSummaries.risk_analyst_go_no_go",
  document_checklist: "createReportModal.sectionSummaries.document_checklist",
  // Technical analysis agents (split from deprecated technical_analyst)
  material_requirements: "createReportModal.sectionSummaries.material_requirements",
  // Teknisk analys template (consolidated 3-section version)
  scope_interfaces: "createReportModal.sectionSummaries.scope_interfaces",
  site_execution_conditions: "createReportModal.sectionSummaries.site_execution_conditions",
  // Kontraktsgranskning Juridik template agents
  legal_review_procurement: "createReportModal.sectionSummaries.legal_review_procurement",
  legal_review_scope: "createReportModal.sectionSummaries.legal_review_scope",
  legal_review_execution: "createReportModal.sectionSummaries.legal_review_execution",
  legal_review_organization: "createReportModal.sectionSummaries.legal_review_organization",
  legal_review_timelines: "createReportModal.sectionSummaries.legal_review_timelines",
  legal_review_liability: "createReportModal.sectionSummaries.legal_review_liability",
  legal_review_financials: "createReportModal.sectionSummaries.legal_review_financials",
  legal_review_inspection: "createReportModal.sectionSummaries.legal_review_inspection",
  legal_review_termination: "createReportModal.sectionSummaries.legal_review_termination",
  legal_review_disputes: "createReportModal.sectionSummaries.legal_review_disputes",
  legal_review_misc: "createReportModal.sectionSummaries.legal_review_misc",
};

function createSection(
  agentType: string,
  headerKey: string,
  blockType: TemplateSection["blockType"],
): TemplateSection {
  const summaryKey = SUMMARY_KEYS[agentType];
  if (!summaryKey) {
    throw new Error(`Missing section summary translation key for agent type: ${agentType}`);
  }

  return {
    agentType,
    headerKey,
    summaryKey,
    blockType,
    instructionExampleKey: INSTRUCTION_EXAMPLE_KEYS[agentType],
  };
}

/**
 * Additional sections not included in any template but available via "add section".
 * These agents exist in the backend but are only accessible by manually adding them.
 *
 * IMPORTANT: These sections are visible to ALL users regardless of org. Do NOT add
 * org-restricted sections here — they should only exist inside their template's
 * sections JSONB in the DB, where RLS controls visibility.
 */
const ADDITIONAL_SECTIONS: TemplateSection[] = [
  createSection("key_findings", "createReportModal.sectionHeaders.keyFindings", "markdown"),
  createSection("timeline_extractor", "createReportModal.sectionHeaders.timeline", "timeline"),
  createSection(
    "question_list_generator",
    "createReportModal.sectionHeaders.questionListGenerator",
    "markdown",
  ),
  createSection(
    "assumptions_reservations_generator",
    "createReportModal.sectionHeaders.assumptionsReservationsGenerator",
    "markdown",
  ),
  createSection(
    "mandatory_requirements_eligibility",
    "createReportModal.sectionHeaders.mandatoryRequirementsEligibility",
    "requirements",
  ),
  createSection(
    "mandatory_requirements_execution",
    "createReportModal.sectionHeaders.mandatoryRequirementsExecution",
    "requirements",
  ),
  createSection(
    "missing_documents_detector",
    "createReportModal.sectionHeaders.missingDocuments",
    "markdown",
  ),
  // Sections available as add-ons (removed from default templates for focus)
  createSection(
    "contradiction_detector",
    "createReportModal.sectionHeaders.contradictions",
    "contradictions",
  ),
  createSection(
    "documentation_requirements",
    "createReportModal.sectionHeaders.documentationRequirements",
    "markdown",
  ),
  createSection("commercial_terms", "createReportModal.sectionHeaders.commercialTerms", "markdown"),
];

/**
 * Extract ordered array of agent types from a template.
 * Used as `selected_templates` when calling the backend.
 */
export function getAgentTypes(template: DocumentTemplate): string[] {
  return template.sections.map((s) => s.agentType);
}

/**
 * Get all unique sections from all templates plus additional standalone sections.
 * Used for the "add section" selector.
 *
 * VISIBILITY: `templates` is RLS-filtered — only templates the user's org can see
 * are included. Sections from org-restricted templates are therefore automatically
 * hidden. A section is visible if ANY visible template contains it, or if it's in
 * ADDITIONAL_SECTIONS below.
 */
export function getAllSections(templates: DocumentTemplate[]): TemplateSection[] {
  const sectionMap = new Map<string, TemplateSection>();
  templates.forEach((template) => {
    template.sections.forEach((section) => {
      if (!sectionMap.has(section.agentType)) {
        sectionMap.set(section.agentType, section);
      }
    });
  });
  ADDITIONAL_SECTIONS.forEach((section) => {
    if (!sectionMap.has(section.agentType)) {
      sectionMap.set(section.agentType, section);
    }
  });
  // Note: Final alphabetical sorting by translated name happens in CreateAIDocumentModal
  return Array.from(sectionMap.values());
}
